import { apiFetch } from '@/lib/api-client';

/**
 * API client for the Phase 2J.5 backend notifications endpoints.
 * All endpoints are JwtAuthGuard-only (NOT @RequireVerifiedEmail —
 * intentional, since verification reminders are themselves
 * notifications and must reach unverified users; documented as a
 * known §12 EmailVerifiedGuard divergence at Batch 9C Gate-0).
 */

export type NotificationCategory =
  | 'story_related'
  | 'tips'
  | 'news'
  // The backend enum has more categories — these are the ones the PWA
  // currently consumes. Any unknown category renders with the default
  // styling (no special treatment).
  | string;

export interface NotificationDto {
  id: string;
  category: NotificationCategory;
  title: string;
  body: string;
  ctaUrl: string | null;
  /** Null = unread; ISO string = read + when. */
  readAt: string | null;
  createdAt: string;
}

export interface ListNotificationsResponse {
  items: NotificationDto[];
  total: number;
}

export function listNotifications(): Promise<ListNotificationsResponse> {
  // Backend returns a bare Notification[] array (notifications.controller.ts
  // line 19-26 → notifications.service.ts line 62-70 Promise<Notification[]>);
  // we wrap it into the { items, total } envelope shape the PWA renderer
  // expects. Pre-fix the PWA assumed the backend returned the envelope, so
  // setItems(undefined) → items.length threw TypeError → Sentry-ErrorBoundary
  // rendered the "Oops! Da ist etwas schiefgelaufen" page on every bell-tap.
  return apiFetch<NotificationDto[]>('/api/notifications', {
    method: 'GET',
  }).then((items) => ({ items, total: items.length }));
}

export function getUnreadCount(): Promise<{ count: number }> {
  return apiFetch<{ count: number }>('/api/notifications/unread-count', {
    method: 'GET',
  });
}

export function markRead(id: string): Promise<NotificationDto> {
  return apiFetch<NotificationDto>(`/api/notifications/${id}/read`, {
    method: 'PATCH',
  });
}

export function markAllRead(): Promise<{ count: number }> {
  return apiFetch<{ count: number }>('/api/notifications/read-all', {
    method: 'PATCH',
  });
}
